import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Paper,
  Dialog,
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Checkbox,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  MenuItem,
} from "@mui/material";
import {
  RemoveRedEye,
  DeleteOutlineRounded,
  WarningAmberRounded,
  ArticleOutlined,
  LockOutlined,
  PauseRounded,
  PlayArrowRounded,
  CreateNewFolderOutlined,
  FolderOutlined,
  ExpandMoreRounded,
  Close,
  Instagram,
  YouTube,
  Twitter,
  Facebook,
  LinkedIn,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { StopUpdates, ResumeUpdates } from "../../../react-query/services/reportServices";
import { useFetchReportGroups, useCreateReportGroup } from "../../../react-query/hooks/useReportGroups";
import reportGroupServices from "../../../react-query/services/reportGroupServices";
import { displayPlatformName } from "../../common/displayPlatformName";

const pulseGreen = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.5); }
  55%       { box-shadow: 0 0 0 5px rgba(34,197,94,0); }
`;

// Platform-icon convention shared with PlanSummary.jsx's ListTabs — keyed by
// lowercase platform name, used for a group row's small stacked platform icons.
const PLATFORM_ICONS = {
  instagram: Instagram,
  youtube: YouTube,
  twitter: Twitter,
  facebook: Facebook,
  linkedin: LinkedIn,
};

// Renders one individual report's cells — shared by top-level report rows and
// a group's nested (expanded) child reports, so that shape only exists once.
// `nested` just indents the name and skips the row-number/select cells; every
// other cell (platform, live posts, status + toggle, view/delete) is identical.
function ReportRow({
  report, rowNumber, nested, selectable, selected, onToggleSelect,
  stopped, isToggling, disableToggle, onToggleUpdates, onView, onDelete,
}) {
  return (
    <TableRow
      sx={{
        bgcolor: nested ? "#FAFBFC" : "transparent",
        "&:last-child td": { borderBottom: 0 },
        "&:hover": { bgcolor: nested ? "#F3F4F6" : "#FAFAFA" },
        transition: "background 0.1s ease",
      }}
    >
      <TableCell padding="checkbox">
        {selectable && (
          <Checkbox
            size="small"
            checked={selected}
            onChange={onToggleSelect}
            sx={{ color: "#D1D5DB", "&.Mui-checked": { color: "#FF5200" } }}
          />
        )}
      </TableCell>

      <TableCell align="center" sx={{ color: "#9CA3AF", fontSize: 13, fontWeight: 500 }}>
        {nested ? "" : rowNumber}
      </TableCell>

      <TableCell sx={{ fontWeight: 500, color: "#111827", fontSize: {xs:12,sm:14}, maxWidth: 220 }}>
        <Box sx={{ pl: nested ? 3.5 : 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {report.name}
        </Box>
      </TableCell>

      <TableCell align="center">
        <Chip
          label={displayPlatformName(report?.platform?.name) ?? "—"}
          size="small"
          sx={{
            fontSize: 12,
            fontWeight: 500,
            bgcolor: "rgba(255,82,0,0.08)",
            color: "#FF5200",
            border: "1px solid rgba(255,82,0,0.2)",
            borderRadius: "8px",
          }}
        />
      </TableCell>

      <TableCell align="center">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
          <ArticleOutlined sx={{ fontSize: 15, color: "#9CA3AF" }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
            {report.postCount ?? 0}
          </Typography>
        </Box>
      </TableCell>

      <TableCell align="center">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: {xs:0.5,sm:1} }}>
          <Box
            sx={{
              width: 8, height: 8, borderRadius: "50%",
              bgcolor: stopped ? "#EF4444" : "#22C55E",
              flexShrink: 0,
              animation: stopped ? "none" : `${pulseGreen} 2.2s ease-in-out infinite`,
            }}
          />
          <Box
            sx={{
              px: 1.2, py: 0.35, borderRadius: "20px",
              border: `1px solid ${stopped ? "rgba(239,68,68,0.25)" : "rgba(34,197,94,0.25)"}`,
              bgcolor: stopped ? "rgba(239,68,68,0.07)" : "rgba(34,197,94,0.07)",
              minWidth: 58,
            }}
          >
            <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: stopped ? "#DC2626" : "#16A34A", letterSpacing: "0.02em", lineHeight: 1 }}>
              {stopped ? "Stopped" : "Active"}
            </Typography>
          </Box>
          <Tooltip title={stopped ? "Resume future updates" : "Stop future updates"} arrow placement="top">
            <span>
              <IconButton
                size="small"
                onClick={onToggleUpdates}
                disabled={disableToggle}
                sx={{
                  p: "4px", borderRadius: "8px",
                  border: `1px solid ${stopped ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.25)"}`,
                  bgcolor: stopped ? "rgba(34,197,94,0.06)" : "rgba(239,68,68,0.05)",
                  color: stopped ? "#16A34A" : "#DC2626",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    bgcolor: stopped ? "rgba(34,197,94,0.14)" : "rgba(239,68,68,0.12)",
                    borderColor: stopped ? "rgba(34,197,94,0.5)" : "rgba(239,68,68,0.45)",
                    transform: "scale(1.08)",
                  },
                  "&.Mui-disabled": { opacity: 0.45, transform: "none" },
                }}
              >
                {isToggling ? (
                  <CircularProgress size={12} thickness={5} sx={{ color: stopped ? "#16A34A" : "#DC2626" }} />
                ) : stopped ? (
                  <PlayArrowRounded sx={{ fontSize: 14 }} />
                ) : (
                  <PauseRounded sx={{ fontSize: 14 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </TableCell>

      <TableCell align="center">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
          <Button
            variant="contained"
            onClick={onView}
            sx={{
              background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)",
              borderRadius: "20px", minWidth: 0, px: 1.5, py: 0.7,
              boxShadow: "0 3px 10px rgba(255,82,0,0.3)",
              "&:hover": { background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)", opacity: 0.9 },
            }}
          >
            <RemoveRedEye sx={{ fontSize: 17 }} />
          </Button>
          <IconButton
            size="small"
            onClick={onDelete}
            sx={{
              color: "#9CA3AF", border: "1px solid #E5E7EB", borderRadius: "20px", p: "6px",
              transition: "all 0.15s ease",
              "&:hover": { color: "#EF4444", borderColor: "rgba(239,68,68,0.35)", background: "rgba(239,68,68,0.06)" },
            }}
          >
            <DeleteOutlineRounded sx={{ fontSize: 17 }} />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  );
}

// rows: a mix of individual reports and report-group rows (rowType: 'group'),
// the latter identified in the UI by a small folder icon next to their name —
// see Reports.jsx's groupRows mapping for the normalized group row shape.
export default function ReportsTableComponent({ rows, loading, onDelete, onDeleteGroup, currentUserId }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [pendingDelete, setPendingDelete] = useState(null);
  const [notAllowedReport, setNotAllowedReport] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [togglingId, setTogglingId] = useState(null);
  const [stoppedOverrides, setStoppedOverrides] = useState({});
  const [expandedGroupIds, setExpandedGroupIds] = useState(() => new Set());

  const toggleExpanded = (groupId) => {
    setExpandedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
      return next;
    });
  };

  // ── Bulk "Group selected" ────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupMode, setGroupMode] = useState("existing"); // 'existing' | 'new'
  const [pickedGroupId, setPickedGroupId] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [grouping, setGrouping] = useState(false);
  const [groupError, setGroupError] = useState(null);

  const { data: groupsResp } = useFetchReportGroups();
  const groupOptions = groupsResp?.data ?? [];
  const createGroupMutation = useCreateReportGroup();

  React.useEffect(() => { setPage(0); }, [rows]);
  // Only individual reports are selectable for bulk grouping — group rows have
  // no checkbox at all (see the render below), so they never end up here.
  // Reports here are always the ungrouped flat list (Reports.jsx filters with
  // ?ungrouped=true), so a selected report can never already belong to a
  // different group — no "move? are you sure" confirmation is needed before
  // attaching.
  React.useEffect(() => {
    setSelectedIds((prev) => {
      const validIds = new Set(rows.map((r) => r._id));
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectablePageRows = (pageRows) => pageRows.filter((r) => r.rowType !== "group");
  const allOnPageSelected = (pageRows) => {
    const selectable = selectablePageRows(pageRows);
    return selectable.length > 0 && selectable.every((r) => selectedIds.has(r._id));
  };
  const toggleSelectAllOnPage = (pageRows) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const selectable = selectablePageRows(pageRows);
      const allSelected = selectable.length > 0 && selectable.every((r) => next.has(r._id));
      selectable.forEach((r) => { if (allSelected) next.delete(r._id); else next.add(r._id); });
      return next;
    });
  };

  const handleOpenGroupModal = () => {
    setGroupError(null);
    setGroupMode("existing");
    setPickedGroupId("");
    setNewGroupName("");
    setGroupModalOpen(true);
  };
  const handleCloseGroupModal = () => {
    if (grouping) return;
    setGroupModalOpen(false);
  };

  const handleConfirmGroup = async () => {
    setGroupError(null);
    let targetGroupId = pickedGroupId;
    if (groupMode === "new") {
      if (!newGroupName.trim()) { setGroupError("Enter a group name."); return; }
    } else if (!targetGroupId) {
      setGroupError("Please select a group.");
      return;
    }
    setGrouping(true);
    try {
      if (groupMode === "new") {
        const createRes = await createGroupMutation.mutateAsync({ name: newGroupName.trim() });
        targetGroupId = createRes?.data?._id || createRes?.data?.id;
      }
      if (!targetGroupId) throw new Error("Could not determine target group.");
      // Independent attach calls — fire concurrently instead of one round-trip at a time.
      await Promise.all(
        [...selectedIds].map((reportId) => reportGroupServices.AttachReportToGroup(targetGroupId, reportId))
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["fetchReportGroups"] }),
        queryClient.invalidateQueries({ queryKey: ["fetchReports"] }),
      ]);
      setSelectedIds(new Set());
      setGroupModalOpen(false);
    } catch (err) {
      setGroupError(err?.response?.data?.message || err?.message || "Failed to group selected reports. Please try again.");
    } finally {
      setGrouping(false);
    }
  };

  const paginatedRows = rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const isUpdatesStopped = (report) => {
    if (report._id in stoppedOverrides) return stoppedOverrides[report._id];
    return report.autoRefreshStopped === true;
  };

  const handleToggleUpdates = async (report) => {
    if (togglingId) return;
    const currentStopped = isUpdatesStopped(report);
    setTogglingId(report._id);
    setStoppedOverrides((prev) => ({ ...prev, [report._id]: !currentStopped }));
    try {
      if (currentStopped) {
        await ResumeUpdates(report._id);
      } else {
        await StopUpdates(report._id);
      }
      // fetchReportGroups too — a toggled report might be a group's expanded
      // child, whose parent row shows a live/stopped count computed server-side.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["fetchReports"] }),
        queryClient.invalidateQueries({ queryKey: ["fetchReportGroups"] }),
      ]);
      setStoppedOverrides((prev) => {
        const next = { ...prev };
        delete next[report._id];
        return next;
      });
    } catch {
      setStoppedOverrides((prev) => ({ ...prev, [report._id]: currentStopped }));
    } finally {
      setTogglingId(null);
    }
  };

  const handleDeleteClick = (row) => {
    if (row.rowType === "group") {
      onDeleteGroup(row);
      return;
    }
    const isCreator = currentUserId && String(row.createdby?._id) === String(currentUserId);
    if (!isCreator) {
      setNotAllowedReport(row);
    } else {
      setPendingDelete(row);
    }
  };

  const handleView = (row) => {
    if (row.rowType === "group") {
      navigate(`/reports/groups/${row._id}/dashboard`);
      return;
    }
    navigate(`/reports/${row._id}/dashboard`, { state: { report: row } });
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDelete || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await onDelete(pendingDelete._id);
      // Harmless if this report wasn't grouped — refreshes the parent group's
      // report count/status breakdown when it was.
      queryClient.invalidateQueries({ queryKey: ["fetchReportGroups"] });
      setPendingDelete(null);
    } catch {
      setDeleteError("Failed to delete report. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    if (deleting) return;
    setPendingDelete(null);
    setDeleteError(null);
  };

  const headCell = {
    fontWeight: 600,
    fontSize: 12,
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    py: 1.5,
    borderBottom: "2px solid #F3F4F6",
  };

  return (
    <>
      {/* ── Bulk "Group selected" action bar ────────────────────────────────── */}
      {selectedIds.size > 0 && (
        <Box
          sx={{
            mt: 2, mb: -1, px: 2.5, py: 1.25,
            bgcolor: "rgba(255,82,0,0.05)",
            border: "1px solid rgba(255,82,0,0.25)",
            borderRadius: "12px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexWrap: "wrap", gap: 1,
          }}
        >
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#111827" }}>
            {selectedIds.size} report{selectedIds.size !== 1 ? "s" : ""} selected
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              size="small"
              onClick={() => setSelectedIds(new Set())}
              sx={{ color: "#6B7280", textTransform: "none", fontSize: 13 }}
            >
              Clear
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<CreateNewFolderOutlined sx={{ fontSize: 16 }} />}
              onClick={handleOpenGroupModal}
              sx={{
                textTransform: "none", fontWeight: 600, fontSize: 13,
                background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)",
                borderRadius: "20px", px: 2,
                boxShadow: "0 3px 10px rgba(255,82,0,0.3)",
                "&:hover": { background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)", opacity: 0.9 },
              }}
            >
              Group Selected
            </Button>
          </Box>
        </Box>
      )}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          mt: 2,
          border: "1px solid #E5E7EB",
          borderRadius: "14px",
          overflowX: "auto",
          "&::-webkit-scrollbar": { height: 6 },
        }}
      >
        <Table aria-label="reports table" sx={{ minWidth: 900 }}>
          <TableHead sx={{ bgcolor: "#F9FAFB" }}>
            <TableRow>
              <TableCell padding="checkbox" sx={{ borderBottom: "2px solid #F3F4F6" }}>
                <Checkbox
                  size="small"
                  checked={allOnPageSelected(paginatedRows)}
                  indeterminate={!allOnPageSelected(paginatedRows) && selectablePageRows(paginatedRows).some(r => selectedIds.has(r._id))}
                  onChange={() => toggleSelectAllOnPage(paginatedRows)}
                  sx={{ color: "#D1D5DB", "&.Mui-checked": { color: "#FF5200" }, "&.MuiCheckbox-indeterminate": { color: "#FF5200" } }}
                />
              </TableCell>
              <TableCell align="center" sx={headCell}>#</TableCell>
              <TableCell sx={headCell}>Report Name</TableCell>
              <TableCell align="center" sx={headCell}>Platform</TableCell>
              <TableCell align="center" sx={headCell}>Posts Live</TableCell>
              <TableCell align="center" sx={headCell}>Status</TableCell>
              <TableCell align="center" sx={headCell}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading &&
              paginatedRows.map((row, index) => {
                const isGroup = row.rowType === "group";

                if (!isGroup) {
                  return (
                    <ReportRow
                      key={row._id ?? index}
                      report={row}
                      rowNumber={page * rowsPerPage + index + 1}
                      nested={false}
                      selectable
                      selected={selectedIds.has(row._id)}
                      onToggleSelect={() => toggleSelected(row._id)}
                      stopped={isUpdatesStopped(row)}
                      isToggling={togglingId === row._id}
                      disableToggle={!!togglingId}
                      onToggleUpdates={() => handleToggleUpdates(row)}
                      onView={() => handleView(row)}
                      onDelete={() => handleDeleteClick(row)}
                    />
                  );
                }

                const expanded = expandedGroupIds.has(row._id);
                const childReports = row.reports || [];

                return (
                  <React.Fragment key={row._id ?? index}>
                    <TableRow
                      sx={{
                        "&:last-child td": { borderBottom: 0 },
                        "&:hover": { bgcolor: "#FAFAFA" },
                        transition: "background 0.1s ease",
                      }}
                    >
                      {/* Select — groups can't be bulk-attached to another group */}
                      <TableCell padding="checkbox" />

                      {/* # */}
                      <TableCell align="center" sx={{ color: "#9CA3AF", fontSize: 13, fontWeight: 500 }}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>

                      {/* Name — expand chevron + folder icon identify a report group row */}
                      <TableCell sx={{ fontWeight: 500, color: "#111827", fontSize: {xs:12,sm:14}, maxWidth: 220 }}>
                        <Box
                          onClick={() => childReports.length > 0 && toggleExpanded(row._id)}
                          sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: childReports.length > 0 ? "pointer" : "default" }}
                        >
                          {childReports.length > 0 ? (
                            <ExpandMoreRounded
                              sx={{
                                fontSize: 19, color: "#9CA3AF", flexShrink: 0,
                                transition: "transform 0.15s ease",
                                transform: expanded ? "rotate(180deg)" : "rotate(-90deg)",
                              }}
                            />
                          ) : (
                            <Box sx={{ width: 19, flexShrink: 0 }} />
                          )}
                          <Tooltip title="Report group" arrow>
                            <FolderOutlined sx={{ fontSize: 16, color: "#FF5200", flexShrink: 0 }} />
                          </Tooltip>
                          <Box component="span" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {row.name}
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Platform — a group can span several, shown as stacked icons */}
                      <TableCell align="center">
                        {(row.platforms || []).length > 0 ? (
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                            {row.platforms.map((p) => {
                              const Icon = PLATFORM_ICONS[(p || "").toLowerCase()];
                              return Icon ? <Icon key={p} sx={{ fontSize: 16, color: "#9CA3AF" }} /> : null;
                            })}
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>—</Typography>
                        )}
                      </TableCell>

                      {/* Posts Live — total posts across every report the group contains */}
                      <TableCell align="center">
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.15 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <ArticleOutlined sx={{ fontSize: 15, color: "#9CA3AF" }} />
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                              {row.totalPosts ?? 0}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: 10.5, color: "#9CA3AF" }}>
                            {row.reportCount ?? 0} report{(row.reportCount ?? 0) !== 1 ? "s" : ""}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Status — active vs. stopped breakdown across the group's reports */}
                      <TableCell align="center">
                        {!row.activeCount && !row.stoppedCount ? (
                          <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>—</Typography>
                        ) : (
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75 }}>
                            {row.activeCount > 0 && (
                              <Tooltip title="Active reports" arrow>
                                <Box sx={{
                                  display: "flex", alignItems: "center", gap: 0.4, px: 1, py: 0.3,
                                  borderRadius: "20px", border: "1px solid rgba(34,197,94,0.25)", bgcolor: "rgba(34,197,94,0.07)",
                                }}>
                                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#22C55E", flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#16A34A" }}>{row.activeCount}</Typography>
                                </Box>
                              </Tooltip>
                            )}
                            {row.stoppedCount > 0 && (
                              <Tooltip title="Stopped reports" arrow>
                                <Box sx={{
                                  display: "flex", alignItems: "center", gap: 0.4, px: 1, py: 0.3,
                                  borderRadius: "20px", border: "1px solid rgba(239,68,68,0.25)", bgcolor: "rgba(239,68,68,0.07)",
                                }}>
                                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#EF4444", flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#DC2626" }}>{row.stoppedCount}</Typography>
                                </Box>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </TableCell>

                      {/* Action */}
                      <TableCell align="center">
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                          <Button
                            variant="contained"
                            onClick={() => handleView(row)}
                            sx={{
                              background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)",
                              borderRadius: "20px", minWidth: 0, px: 1.5, py: 0.7,
                              boxShadow: "0 3px 10px rgba(255,82,0,0.3)",
                              "&:hover": { background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)", opacity: 0.9 },
                            }}
                          >
                            <RemoveRedEye sx={{ fontSize: 17 }} />
                          </Button>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(row)}
                            sx={{
                              color: "#9CA3AF", border: "1px solid #E5E7EB", borderRadius: "20px", p: "6px",
                              transition: "all 0.15s ease",
                              "&:hover": { color: "#EF4444", borderColor: "rgba(239,68,68,0.35)", background: "rgba(239,68,68,0.06)" },
                            }}
                          >
                            <DeleteOutlineRounded sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Expanded — the group's own reports, inline, same as the parent report dashboard's tabs */}
                    {expanded && childReports.map((child) => {
                      const childReport = {
                        _id: child.id,
                        name: child.name,
                        platform: child.platform,
                        postCount: child.postCount,
                        autoRefreshStopped: child.autoRefreshStopped,
                        createdby: child.createdby,
                      };
                      return (
                        <ReportRow
                          key={child.id}
                          report={childReport}
                          nested
                          selectable={false}
                          stopped={isUpdatesStopped(childReport)}
                          isToggling={togglingId === childReport._id}
                          disableToggle={!!togglingId}
                          onToggleUpdates={() => handleToggleUpdates(childReport)}
                          onView={() => handleView(childReport)}
                          onDelete={() => handleDeleteClick(childReport)}
                        />
                      );
                    })}
                  </React.Fragment>
                );
              })}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          sx={{
            borderTop: "1px solid #F3F4F6",
            "& .MuiTablePagination-toolbar": { flexWrap:'wrap',color: "#6B7280", fontSize: 13,justifyContent:'center' },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              fontSize: 13,
            },
          }}
        />
      </TableContainer>

      {/* ── Delete Confirmation Dialog ──────────────────────────────────────── */}
      <Dialog
        open={Boolean(pendingDelete)}
        onClose={handleCancelDelete}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            border: "1px solid #E5E7EB",
            boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
          },
        }}
      >
        <Box sx={{ height: 4, background: "linear-gradient(90deg, #EF4444 0%, #F87171 100%)", flexShrink: 0 }} />

        <Box sx={{ px: {xs:2,sm:3.5}, pt: 3, pb:{xs:2,sm:3.5} }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                width: 40, height: 40, borderRadius: "11px",
                background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}
            >
              <WarningAmberRounded sx={{ color: "#EF4444", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ color: "#111827", fontWeight: 700, fontSize: 17, lineHeight: 1.2 }}>
                Delete Report?
              </Typography>
              <Typography sx={{ color: "#9CA3AF", fontSize: {xs:10,sm:12}, mt: 0.3 }}>
                This action cannot be undone
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              background: "#F9FAFB", border: "1px solid #E5E7EB",
              borderRadius: "10px", px: 2, py: 1.4, mb: 2.5,
            }}
          >
            <Typography sx={{ color: "#6B7280", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.4 }}>
              Report
            </Typography>
            <Typography sx={{ color: "#111827", fontWeight: 500, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {pendingDelete?.name}
            </Typography>
          </Box>

          <Typography sx={{ color: "#6B7280", fontSize: 13, lineHeight: 1.6, mb: deleteError ? 1.5 : 3 }}>
            The report will be removed from your list. Scheduled data refreshes will also be cancelled so no API quota is consumed.
          </Typography>

          {deleteError && (
            <Box sx={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: "8px", px: 2, py: 1, mb: 2 }}>
              <Typography sx={{ color: "#EF4444", fontSize: 12.5 }}>{deleteError}</Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              fullWidth
              disabled={deleting}
              onClick={handleCancelDelete}
              sx={{
                color: "#6B7280", background: "#fff", borderRadius: "10px", py: 1.1,
                fontSize: 13.5, fontWeight: 500, textTransform: "none",
                border: "1px solid #E5E7EB",
                "&:hover": { background: "#F9FAFB", borderColor: "#D1D5DB" },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              disabled={deleting}
              onClick={handleDeleteConfirm}
              sx={{
                background: "linear-gradient(135deg, #EF4444 0%, #F87171 100%)",
                borderRadius: "10px", py: 1.1, fontSize: 13.5, fontWeight: 600, textTransform: "none",
                boxShadow: "0 4px 14px rgba(239,68,68,0.3)",
                "&:hover": { background: "linear-gradient(135deg, #EF4444 0%, #F87171 100%)", opacity: 0.9, boxShadow: "0 6px 20px rgba(239,68,68,0.4)" },
                "&.Mui-disabled": { background: "#F3F4F6", color: "#9CA3AF", boxShadow: "none" },
              }}
            >
              {deleting ? "Deleting…" : "Yes, Delete"}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* ── Not Allowed Dialog ─────────────────────────────────────────────── */}
      <Dialog
        open={Boolean(notAllowedReport)}
        onClose={() => setNotAllowedReport(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            border: "1px solid #E5E7EB",
            boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
          },
        }}
      >
        <Box sx={{ height: 4, background: "linear-gradient(90deg, #FF5200 0%, #FF8C00 100%)" }} />

        <Box sx={{ px: 3.5, pt: 3, pb: 3.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box
              sx={{
                width: 44, height: 44, borderRadius: "12px", flexShrink: 0,
                background: "linear-gradient(135deg, rgba(255,82,0,0.1), rgba(255,140,0,0.06))",
                border: "1px solid rgba(255,82,0,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <LockOutlined sx={{ color: "#FF5200", fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ color: "#111827", fontWeight: 700, fontSize: 17, lineHeight: 1.2 }}>
                Can't Delete This Report
              </Typography>
              <Typography sx={{ color: "#9CA3AF", fontSize: 12, mt: 0.3 }}>
                You don't have permission to delete
              </Typography>
            </Box>
          </Box>

          <Box sx={{ bgcolor: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: "12px", px: 2, py: 1.8, mb: 3 }}>
            <Typography sx={{ fontSize: 12, color: "#6B7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>
              Created by
            </Typography>
            <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: "#111827" }}>
              {notAllowedReport?.createdby?.fullName ?? "Another team member"}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "#9CA3AF", mt: 0.4 }}>
              Only the creator of a report can delete it.
            </Typography>
          </Box>

          <Button
            fullWidth
            onClick={() => setNotAllowedReport(null)}
            sx={{
              background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)",
              color: "#fff", borderRadius: "12px", py: 1.2,
              fontSize: 14, fontWeight: 600, textTransform: "none",
              boxShadow: "0 4px 14px rgba(255,82,0,0.3)",
              "&:hover": { opacity: 0.9, boxShadow: "0 6px 20px rgba(255,82,0,0.4)" },
            }}
          >
            Got it
          </Button>
        </Box>
      </Dialog>

      {/* ── Group Selected modal ─────────────────────────────────────────────── */}
      <Dialog
        open={groupModalOpen}
        onClose={handleCloseGroupModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            border: "1px solid #E5E7EB",
            boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
          },
        }}
      >
        <Box sx={{ height: 4, background: "linear-gradient(90deg, #FF5200 0%, #FF8C00 100%)" }} />

        <Box sx={{ px: { xs: 2, sm: 3.5 }, pt: 3, pb: 2, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: "11px",
              background: "linear-gradient(135deg, rgba(255,82,0,0.1), rgba(255,140,0,0.05))",
              border: "1px solid rgba(255,82,0,0.18)",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <CreateNewFolderOutlined sx={{ color: "#FF5200", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ color: "#111827", fontWeight: 700, fontSize: 17, lineHeight: 1.2 }}>
                Group {selectedIds.size} Report{selectedIds.size !== 1 ? "s" : ""}
              </Typography>
              <Typography sx={{ color: "#9CA3AF", fontSize: 12, mt: 0.3 }}>
                Add to a parent report group
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={handleCloseGroupModal} disabled={grouping} sx={{ color: "#9CA3AF" }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3.5 }, pb: 3.5 }}>
          <RadioGroup
            value={groupMode}
            onChange={(e) => { setGroupMode(e.target.value); setGroupError(null); }}
            sx={{ mb: 2 }}
          >
            <FormControlLabel
              value="existing"
              disabled={groupOptions.length === 0}
              control={<Radio size="small" sx={{ color: "#D1D5DB", "&.Mui-checked": { color: "#FF5200" } }} />}
              label={<Typography sx={{ fontSize: 13.5 }}>Add to existing group</Typography>}
            />
            {groupMode === "existing" && (
              <TextField
                select
                fullWidth
                size="small"
                value={pickedGroupId}
                onChange={(e) => setPickedGroupId(e.target.value)}
                disabled={groupOptions.length === 0}
                SelectProps={{ displayEmpty: true }}
                sx={{ mb: 2, ml: 4, width: "calc(100% - 32px)" }}
              >
                <MenuItem value="" disabled>
                  {groupOptions.length === 0 ? "No groups yet — create one below" : "Choose a group…"}
                </MenuItem>
                {groupOptions.map((g) => (
                  <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                ))}
              </TextField>
            )}

            <FormControlLabel
              value="new"
              control={<Radio size="small" sx={{ color: "#D1D5DB", "&.Mui-checked": { color: "#FF5200" } }} />}
              label={<Typography sx={{ fontSize: 13.5 }}>Create a new group</Typography>}
            />
            {groupMode === "new" && (
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Lava — Q3 Campaign"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                sx={{ ml: 4, width: "calc(100% - 32px)" }}
              />
            )}
          </RadioGroup>

          {groupError && (
            <Box sx={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: "8px", px: 2, py: 1, mb: 2 }}>
              <Typography sx={{ color: "#EF4444", fontSize: 12.5 }}>{groupError}</Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              fullWidth
              disabled={grouping}
              onClick={handleCloseGroupModal}
              sx={{
                color: "#6B7280", background: "#fff", borderRadius: "10px", py: 1.1,
                fontSize: 13.5, fontWeight: 500, textTransform: "none",
                border: "1px solid #E5E7EB",
                "&:hover": { background: "#F9FAFB", borderColor: "#D1D5DB" },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              disabled={grouping}
              onClick={handleConfirmGroup}
              startIcon={grouping ? <CircularProgress size={14} thickness={5} sx={{ color: "#fff" }} /> : null}
              sx={{
                background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)",
                borderRadius: "10px", py: 1.1, fontSize: 13.5, fontWeight: 600, textTransform: "none",
                boxShadow: "0 4px 14px rgba(255,82,0,0.3)",
                "&:hover": { background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)", opacity: 0.9 },
              }}
            >
              {grouping ? "Grouping…" : "Confirm"}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}
